import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface LineageRow {
  readonly lineageId: string;
  readonly sourceSystem: string;
  readonly transformation: string | null;
  readonly destination: string;
}

/** M26 — Data Lineage Viewer (§18, BR-04). Lineage for every published metric. */
export function M26DataLineageViewerPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<LineageRow[]>([]);
  // No seed code: 'KPI-0001' is not a metric any tenant has, so the screen used
  // to open on EX-VAL-422 before the user had typed anything.
  const [metricCode, setMetricCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!metricCode) return;
    let alive = true;
    setLoading(true);
    apiRequest<{ data: LineageRow[] }>(`/v1/dwh/governance/lineage?metricCode=${encodeURIComponent(metricCode)}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, metricCode]);

  return (
    <main aria-label="data-lineage-viewer">
      <h1>{t('m26.lineage.title')}</h1>
      <label htmlFor="metric-code">{t('m26.lineage.metricCodeLabel')}</label>
      <input id="metric-code" value={metricCode} onChange={(e) => setMetricCode(e.target.value)} />
      {error && <p role="alert">{error}</p>}
      {!metricCode ? <p aria-label="lineage-prompt">{t('m26.lineage.prompt')}</p> : loading ? <p>{t('m26.lineage.loading')}</p> : (
        <>
          <p aria-label="lineage-count">{t('m26.lineage.count', { count: rows.length })}</p>
          <table aria-label="lineage-table">
            <thead><tr><th>{t('m26.lineage.colSource')}</th><th>{t('m26.lineage.colTransformation')}</th><th>{t('m26.lineage.colDestination')}</th></tr></thead>
            <tbody>{rows.map((l) => <tr key={l.lineageId}><td>{l.sourceSystem}</td><td>{l.transformation ?? '—'}</td><td>{l.destination}</td></tr>)}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
